/**
 * Browser shim for window.freshvibe.
 *
 * The shared Electron components call window.freshvibe for IPC operations.
 * This shim provides browser-native equivalents:
 *   - Agent/session/terminal control → WebSocket /api/core
 *   - Session updates → replay + streamed core events
 *   - Settings → localStorage
 */

import type {
  AgentCapabilities,
  AgentSessionInfo,
  AgentState,
  AuthChallenge,
  FreshVibeAPI,
  PreviewMetadata,
  PtyConnectionStatus,
  RuntimeBootstrapRequest,
  RuntimeState,
} from "@/types";
import {
  nextAgentReconnectDelay,
} from "@web/lib/agent-reconnect";
import { defaultHomeForProvider } from "@web/lib/onboarding";
import {
  captureAgentException,
  captureAgentTelemetry,
  classifyAgentError,
} from "@web/lib/agent-telemetry";

// ─── Event emitter for server→client callbacks ──────────────────────────
type Callback = (...args: any[]) => void;
const listeners: Record<string, Set<Callback>> = {};

function on(event: string, cb: Callback): () => void {
  const set = (listeners[event] ??= new Set());
  set.add(cb);
  return () => { set.delete(cb); };
}

function emit(event: string, ...args: any[]) {
  const cbs = listeners[event];
  if (cbs) for (const cb of cbs) cb(...args);
}

// ─── Agent WebSocket (ACP events from server) ───────────────────────────
let agentWs: WebSocket | null = null;
let agentWsOpenPromise: Promise<void> | null = null;
let resolveAgentWsOpen: (() => void) | null = null;
let rejectAgentWsOpen: ((err: Error) => void) | null = null;
let agentWsGatewayReadyPromise: Promise<void> | null = null;
let resolveAgentWsGatewayReady: (() => void) | null = null;
let agentShouldReconnect = false;
let agentReconnectTimer: ReturnType<typeof setTimeout> | null = null;
let agentReconnectAttempts = 0;
const AGENT_MAX_RECONNECT = 6;
const corePendingRequests = new Map<string, {
  resolve: (payload: unknown) => void;
  reject: (error: Error) => void;
  timeoutId: ReturnType<typeof setTimeout> | null;
  type: string;
  channel: "control" | "terminal" | "system";
  startedAt: number;
}>();
let coreRequestCounter = 0;
let latestCoreSeq = 0;
let latestTerminalId: string | null = null;
let activePromptTelemetry: {
  requestId: string;
  startedAt: number;
  responseStarted: boolean;
} | null = null;
let lastPromptRequestId: string | null = null;
type PromptCompletion = {
  promise: Promise<void>;
  resolve: () => void;
  reject: (error: Error) => void;
};
const promptCompletions = new Map<string, PromptCompletion>();
// True only when THIS page created the adopted terminal via terminal.create.
// A terminal adopted from a session.resume snapshot is a pre-existing, shared,
// persistent shell owned by the workspace runtime — this page merely re-attached
// to it. On unmount (e.g. a StrictMode mount/cleanup/remount, or a hard reload
// while the shell is live) we must NOT close a terminal we only re-attached to:
// the core keeps it alive across websocket detach, so tearing it down here would
// SIGTERM/SIGKILL the live shell out from under the very page that just adopted it.
let termCreatedByThisPage = false;
let latestPorts: number[] = [];
let latestPreviewMetadata: PreviewMetadata | null = null;
const DEFAULT_RUNTIME_STATE: RuntimeState = {
  status: "idle",
  reason: null,
  stage: null,
  progress: 0,
  headline: null,
  detail: null,
  error: null,
  workspace: {
    provider: null,
    workspaceId: null,
  },
};
let latestRuntimeState: RuntimeState = { ...DEFAULT_RUNTIME_STATE };
const CORE_REQUEST_TIMEOUT_MS = Number((globalThis as any).__FRESHVIBE_CORE_REQUEST_TIMEOUT_MS ?? 8_000);
const CORE_CONNECT_TIMEOUT_MS = Number((globalThis as any).__FRESHVIBE_CORE_CONNECT_TIMEOUT_MS ?? 8_000);
const CORE_SESSION_START_TIMEOUT_MS = Number(
  (globalThis as any).__FRESHVIBE_CORE_SESSION_START_TIMEOUT_MS ?? Math.max(CORE_REQUEST_TIMEOUT_MS, 30_000),
);

function getCoreRequestTimeoutMs(type: string): number | null {
  switch (type) {
    case "hello":
    case "session.resume":
    case "agent.install":
    case "runtime.bootstrap":
    case "prompt.send":
      return null;
    case "session.start":
      return CORE_SESSION_START_TIMEOUT_MS;
    default:
      return CORE_REQUEST_TIMEOUT_MS;
  }
}

function getAgentWsUrl(): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}/api/core`;
}

function clearAgentReconnectTimer() {
  if (agentReconnectTimer) {
    clearTimeout(agentReconnectTimer);
    agentReconnectTimer = null;
  }
}

function createPromptCompletion(): PromptCompletion {
  let resolve!: () => void;
  let reject!: (error: Error) => void;
  const promise = new Promise<void>((resolvePromise, rejectPromise) => {
    resolve = resolvePromise;
    reject = rejectPromise;
  });
  // A transport close can settle this before sendPrompt reaches its second
  // await. Attach a handler now so that diagnostic rejection never becomes an
  // unhandled promise while the caller is switching sessions.
  void promise.catch(() => {});
  return { promise, resolve, reject };
}

function settlePromptCompletion(requestId: string, error?: Error): void {
  const completion = promptCompletions.get(requestId);
  if (!completion) return;
  promptCompletions.delete(requestId);
  if (error) {
    completion.reject(error);
  } else {
    completion.resolve();
  }
}

function rejectAllPromptCompletions(error: Error): void {
  for (const requestId of promptCompletions.keys()) {
    if (activePromptTelemetry?.requestId === requestId) {
      captureAgentTelemetry("agent_prompt_failed", {
        ...telemetryWorkspaceContext(),
        request_id: requestId,
        error_code: classifyAgentError(error),
        duration_ms: Date.now() - activePromptTelemetry.startedAt,
      });
      captureAgentException(error, {
        ...telemetryWorkspaceContext(),
        request_id: requestId,
        request_type: "prompt.send",
        agent_failure_stage: "prompt_completion_rejected",
        error_code: classifyAgentError(error),
        duration_ms: Date.now() - activePromptTelemetry.startedAt,
      });
      activePromptTelemetry = null;
    }
    settlePromptCompletion(requestId, error);
  }
}

function rejectAllCoreRequests(error: Error) {
  for (const [id, pending] of corePendingRequests) {
    clearTimeout(pending.timeoutId);
    if (pending.type === "prompt.send") {
      captureAgentTelemetry("agent_prompt_failed", {
        ...telemetryWorkspaceContext(),
        request_id: id,
        error_code: classifyAgentError(error),
        duration_ms: Date.now() - pending.startedAt,
      });
      captureAgentException(error, {
        ...telemetryWorkspaceContext(),
        request_id: id,
        request_type: pending.type,
        agent_failure_stage: "pending_request_rejected",
        error_code: classifyAgentError(error),
        duration_ms: Date.now() - pending.startedAt,
      });
      if (activePromptTelemetry?.requestId === id) {
        activePromptTelemetry = null;
      }
    }
    pending.reject(error);
    corePendingRequests.delete(id);
  }
  rejectAllPromptCompletions(error);
}

function resetAgentWsHandshakeState() {
  resolveAgentWsOpen = null;
  rejectAgentWsOpen = null;
  agentWsOpenPromise = null;
  resolveAgentWsGatewayReady = null;
  agentWsGatewayReadyPromise = null;
}

function disableAgentReconnect() {
  agentShouldReconnect = false;
  agentReconnectAttempts = 0;
  clearAgentReconnectTimer();
}

function clearWorkspaceRuntimeState() {
  rejectAllPromptCompletions(new Error("Workspace runtime was reset"));
  latestCoreSeq = 0;
  latestTerminalId = null;
  latestPorts = [];
  latestPreviewMetadata = null;
  latestRuntimeState = { ...DEFAULT_RUNTIME_STATE };
  latestAcpSessionId = null;
  latestAuthChallenge = null;
  latestConnectionStatus = "disconnected";
  lastSelectedAgentPreset = null;
  termAuthBuffer = "";
  lastAutoCopiedAuthCode = null;
  copilotPlaintextPromptAnswered = false;
  activePromptTelemetry = null;
  lastPromptRequestId = null;

  setPtyConnectionStatus("disconnected");
  emit("connection-status", "disconnected");
  emit("ports-changed", []);
  emit("preview-metadata-changed", null);
  emit("runtime-state", latestRuntimeState);
}

function emitAgentDebug(level: "info" | "warn" | "error" | "debug", message: string, data?: unknown) {
  emit("debug-log", {
    id: crypto.randomUUID(),
    timestamp: Date.now(),
    level,
    source: "agent",
    message,
    data,
  });
}

function telemetryWorkspaceContext(): Record<string, string> {
  const workspace = latestRuntimeState.workspace;
  return {
    workspace_provider: workspace.provider || "unknown",
    workspace_id_prefix: workspace.workspaceId?.trim().slice(0, 10) || "unknown",
  };
}

function summarizePromptPayload(payload: unknown): Record<string, string | number | boolean> {
  if (!payload || typeof payload !== "object") {
    return { has_attachments: false, attachment_count: 0, text_length: 0 };
  }
  const record = payload as Record<string, unknown>;
  const attachments = Array.isArray(record.attachments) ? record.attachments : [];
  return {
    has_attachments: attachments.length > 0,
    attachment_count: attachments.length,
    text_length: typeof record.text === "string" ? record.text.length : 0,
  };
}

function sessionUpdateType(value: unknown): string | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const direct = record.sessionUpdate ?? record.session_update;
  return typeof direct === "string" ? direct : null;
}

function reportCoreTransportFailure(message: string) {
  latestConnectionStatus = "error";
  setPtyConnectionStatus("disconnected");
  emit("connection-status", "error");
  emit("error", message);
  const error = new Error(message);
  captureAgentException(error, {
    ...telemetryWorkspaceContext(),
    agent_failure_stage: "core_transport",
    error_code: classifyAgentError(error),
  });
}

function isBenignCoreConnectCloseReason(reason: string | undefined): boolean {
  return reason === "No selected workspace for freshvibe-core";
}

function scheduleAgentWsReconnect(reason: string) {
  if (!agentShouldReconnect || !lastSelectedAgentPreset) return;
  if (agentReconnectTimer) return;

  if (agentReconnectAttempts >= AGENT_MAX_RECONNECT) {
    emitAgentDebug("error", `Agent WebSocket reconnect limit reached (${AGENT_MAX_RECONNECT} attempts)`);
    emit("connection-status", "disconnected");
    return;
  }

  agentReconnectAttempts += 1;
  const delay = nextAgentReconnectDelay(agentReconnectAttempts);
  emit("connection-status", "connecting");
  emitAgentDebug(
    "info",
    `Agent WebSocket reconnect scheduled in ${delay}ms (attempt ${agentReconnectAttempts}/${AGENT_MAX_RECONNECT})`,
    { reason },
  );

  agentReconnectTimer = setTimeout(() => {
    agentReconnectTimer = null;
    void ensureAgentWs().catch((err) => {
      const msg = err instanceof Error ? err.message : String(err);
      emitAgentDebug("warn", `Agent WebSocket reconnect attempt failed: ${msg}`);
      scheduleAgentWsReconnect(msg);
    });
  }, delay);
}

function nextCoreRequestId(): string {
  coreRequestCounter += 1;
  return `core-${coreRequestCounter}`;
}

function applyCoreSnapshot(snapshot: any) {
  if (!snapshot || typeof snapshot !== "object") return;

  const runtimeState = normalizeRuntimeState(snapshot.runtime);
  if (runtimeState) {
    setRuntimeState(runtimeState);
  }

  if (snapshot.agent && typeof snapshot.agent === "object") {
    const agent = snapshot.agent as Record<string, unknown>;
    if (typeof agent.acpSessionId === "string" && agent.acpSessionId) {
      latestAcpSessionId = agent.acpSessionId;
      emit("acp-session-id", agent.acpSessionId);
    }
    if (typeof agent.status === "string") {
      latestConnectionStatus = agent.status;
      emit("connection-status", agent.status);
    }
  }

  if (snapshot.terminal && typeof snapshot.terminal === "object") {
    const terminal = snapshot.terminal as Record<string, unknown>;
    if (typeof terminal.terminalId === "string" && terminal.terminalId) {
      // Re-attaching to a terminal the runtime already owns — not ours to close.
      latestTerminalId = terminal.terminalId;
      termCreatedByThisPage = false;
    }
    if (typeof terminal.status === "string") {
      const status = terminal.status === "connected"
        ? "connected"
        : terminal.status === "connecting"
          ? "connecting"
          : terminal.status === "error"
            ? "error"
            : "disconnected";
      setPtyConnectionStatus(status);
    }
  }

  if (Array.isArray(snapshot.ports)) {
    latestPorts = snapshot.ports.filter((port: unknown): port is number => Number.isInteger(port) && port > 0);
    emit("ports-changed", latestPorts);
  }

  const previewMetadata = normalizePreviewMetadata(snapshot.previewMetadata);
  if (previewMetadata !== undefined) {
    latestPreviewMetadata = previewMetadata;
    emit("preview-metadata-changed", latestPreviewMetadata);
  }

  const workspaceMetadata = normalizeWorkspaceMetadata(snapshot.workspaceMetadata);
  if (workspaceMetadata !== undefined) {
    persistWorkspaceMetadata(workspaceMetadata);
  }
}

function normalizePreviewMetadata(value: unknown): PreviewMetadata | null | undefined {
  if (value == null) {
    return null;
  }
  if (!value || typeof value !== "object") {
    return undefined;
  }

  const candidate = value as Record<string, unknown>;
  const domain = typeof candidate.domain === "string" ? candidate.domain.trim() : "";
  if (!domain) {
    return null;
  }

  const slugs = Array.isArray(candidate.slugs)
    ? candidate.slugs.filter((slug): slug is string => typeof slug === "string" && slug.trim().length > 0)
    : [];
  const defaultPort = Number.isInteger(candidate.defaultPort) && Number(candidate.defaultPort) > 0
    ? Number(candidate.defaultPort)
    : 3000;

  return {
    domain,
    slugs,
    defaultPort,
  };
}

function normalizeRuntimeState(value: unknown): RuntimeState | undefined {
  if (!value || typeof value !== "object") return undefined;

  const candidate = value as Record<string, unknown>;
  const status = candidate.status;
  if (
    status !== "idle"
    && status !== "booting"
    && status !== "ready"
    && status !== "auth_required"
    && status !== "error"
  ) {
    return undefined;
  }

  const reason = candidate.reason;
  const normalizedReason = reason === "cold_start"
    || reason === "reconnect"
    || reason === "auth"
    || reason === "failed"
    ? reason
    : null;
  const stage = candidate.stage;
  const normalizedStage = stage === "creating_workspace"
    || stage === "waiting_for_workspace"
    || stage === "waiting_for_terminal"
    || stage === "installing_starter_project"
    || stage === "installing_agent"
    || stage === "starting_agent"
    ? stage
    : null;

  const workspace = normalizeWorkspaceMetadata(candidate.workspace) ?? latestRuntimeState.workspace;

  return {
    status,
    reason: normalizedReason,
    stage: normalizedStage,
    progress: Number.isFinite(candidate.progress) ? Number(candidate.progress) : 0,
    headline: typeof candidate.headline === "string" && candidate.headline.trim() ? candidate.headline : null,
    detail: typeof candidate.detail === "string" && candidate.detail.trim() ? candidate.detail : null,
    error: typeof candidate.error === "string" && candidate.error.trim() ? candidate.error : null,
    workspace: {
      provider: workspace?.provider ?? null,
      workspaceId: workspace?.workspaceId ?? null,
    },
  };
}

function setRuntimeState(nextState: RuntimeState): void {
  latestRuntimeState = {
    ...nextState,
    workspace: {
      provider: nextState.workspace.provider,
      workspaceId: nextState.workspace.workspaceId,
    },
  };
  emit("runtime-state", latestRuntimeState);
}

function handleCoreEvent(rawEvent: any) {
  if (!rawEvent || typeof rawEvent !== "object") return;

  if (typeof rawEvent.seq === "number") {
    latestCoreSeq = Math.max(latestCoreSeq, rawEvent.seq);
  }

  const payload = rawEvent.payload && typeof rawEvent.payload === "object"
    ? rawEvent.payload as Record<string, unknown>
    : {};

  switch (rawEvent.type) {
    case "status": {
      const nextStatus = typeof payload.status === "string" ? payload.status : "disconnected";
      const previousStatus = latestConnectionStatus;
      latestConnectionStatus = nextStatus;
      if (nextStatus === "ready" || nextStatus === "connecting" || nextStatus === "installing" || nextStatus === "authenticating" || nextStatus === "authenticating:terminal") {
        if (lastSelectedAgentPreset) {
          agentShouldReconnect = true;
        }
        if (nextStatus === "ready") {
          agentReconnectAttempts = 0;
        }
      }
      if (nextStatus === "ready" || nextStatus === "disconnected" || nextStatus === "error") {
        latestAuthChallenge = null;
        termAuthBuffer = "";
        lastAutoCopiedAuthCode = null;
        copilotPlaintextPromptAnswered = false;
      }
      emit("connection-status", nextStatus);
      if (previousStatus !== nextStatus) {
        captureAgentTelemetry("agent_connection_state", {
          ...telemetryWorkspaceContext(),
          status: nextStatus,
        });
      }
      if (nextStatus === "ready" && latestAcpSessionId) {
        emit("acp-session-id", latestAcpSessionId);
      }
      break;
    }
    case "acp-session-id":
      if (typeof payload.sessionId === "string" && payload.sessionId) {
        latestAcpSessionId = payload.sessionId;
        emit("acp-session-id", payload.sessionId);
      }
      break;
    case "session-update": {
      const update = payload.data ?? payload;
      const updateType = sessionUpdateType(update);
      if (activePromptTelemetry && updateType === "agent_message_chunk" && !activePromptTelemetry.responseStarted) {
        activePromptTelemetry.responseStarted = true;
        captureAgentTelemetry("agent_response_started", {
          ...telemetryWorkspaceContext(),
          request_id: activePromptTelemetry.requestId,
          duration_ms: Date.now() - activePromptTelemetry.startedAt,
        });
      }
      if (activePromptTelemetry && updateType === "agent_message_end") {
        const requestId = activePromptTelemetry.requestId;
        const durationMs = Date.now() - activePromptTelemetry.startedAt;
        captureAgentTelemetry("agent_response_completed", {
          ...telemetryWorkspaceContext(),
          request_id: requestId,
          duration_ms: durationMs,
        });
        captureAgentTelemetry("agent_prompt_completed", {
          ...telemetryWorkspaceContext(),
          request_id: requestId,
          duration_ms: durationMs,
        });
        settlePromptCompletion(requestId);
        activePromptTelemetry = null;
      }
      emit("session-update", update);
      break;
    }
    case "permission-request":
      emit("permission-request", payload.data ?? payload);
      break;
    case "auth-challenge": {
      const challenge = payload.data ?? payload;
      if (!challenge || typeof challenge !== "object") {
        latestAuthChallenge = null;
        emit("auth-challenge", challenge ?? null);
        break;
      }

      const candidate = challenge as AuthChallenge;
      const nextChallenge: AuthChallenge = {
        ...candidate,
        // Older cores did not include an id. Keep one stable while terminal
        // output progressively enriches the same challenge.
        id: candidate.id || latestAuthChallenge?.id || crypto.randomUUID(),
      };
      latestAuthChallenge = nextChallenge;
      emit("auth-challenge", nextChallenge);
      break;
    }
    case "models-available":
      emit("models-available", payload.data ?? payload);
      break;
    case "agent-preset":
      if (typeof payload.presetId === "string" && payload.presetId) {
        lastSelectedAgentPreset = payload.presetId;
        agentShouldReconnect = true;
        emit("agent-preset", payload.presetId);
      }
      break;
    case "ports-changed":
      latestPorts = Array.isArray(payload.ports)
        ? payload.ports.filter((port: unknown): port is number => Number.isInteger(port) && port > 0)
        : [];
      emit("ports-changed", latestPorts);
      break;
    case "preview.metadata": {
      const metadata = normalizePreviewMetadata(payload);
      if (metadata !== undefined) {
        latestPreviewMetadata = metadata;
        emit("preview-metadata-changed", latestPreviewMetadata);
      }
      break;
    }
    case "workspace.metadata": {
      const metadata = normalizeWorkspaceMetadata(payload);
      if (metadata !== undefined) {
        persistWorkspaceMetadata(metadata);
      }
      break;
    }
    case "runtime.state": {
      const runtimeState = normalizeRuntimeState(payload);
      if (runtimeState) {
        setRuntimeState(runtimeState);
      }
      break;
    }
    case "error":
      if (typeof payload.message === "string") {
        emit("error", payload.message);
      }
      break;
    case "debug":
      emit("debug-log", {
        id: crypto.randomUUID(),
        timestamp: Date.now(),
        level: payload.level || "info",
        source: "agent",
        message: payload.message,
        data: payload.data,
      });
      break;
    case "terminal.output":
      // Output from a superseded/killed terminal must not leak into the live
      // xterm — otherwise two racing shells' prompts interleave on screen.
      if (typeof payload.data === "string" && isActiveTerminalFrame(payload.terminalId)) {
        detectTerminalAuthChallenge(payload.data);
        emit("pty-output", payload.data);
      }
      break;
    case "terminal.created": {
      // Adoption is owned by the terminal.create response (see connectTermWs),
      // which tracks the spawn generation. Here we only confirm connectivity for
      // the already-adopted terminal, and reap any terminal we never adopted
      // (e.g. the loser of a StrictMode mount/kill/remount race).
      const createdId = typeof payload.terminalId === "string" ? payload.terminalId : "";
      if (latestTerminalId && createdId && createdId !== latestTerminalId) {
        reapOrphanTerminal(createdId, "terminal.created for a superseded terminal");
        break;
      }
      if (!isActiveTerminalFrame(payload.terminalId)) break;
      setPtyConnectionStatus("connected");
      flushPendingTermCommands();
      break;
    }
    case "terminal.status": {
      if (!isActiveTerminalFrame(payload.terminalId)) break;
      const terminalStatus = payload.status;
      if (terminalStatus === "connected") {
        termReconnectAttempts = 0;
        setPtyConnectionStatus("connected");
        flushPendingTermCommands();
      } else if (terminalStatus === "connecting") {
        setPtyConnectionStatus("connecting");
      } else if (terminalStatus === "error") {
        setPtyConnectionStatus("error");
      } else {
        setPtyConnectionStatus("disconnected");
      }
      break;
    }
    case "terminal.closed":
    case "terminal.exit":
      // A stale terminal's exit must never clear the live terminal's id or tell
      // the UI the shell exited. Only the adopted terminal's exit is real.
      if (!isActiveTerminalFrame(payload.terminalId)) break;
      latestTerminalId = null;
      termAdoptGeneration += 1;
      setPtyConnectionStatus("disconnected");
      emit("pty-exit", payload.exitCode ?? 0);
      break;
  }
}

function handleCoreResponse(rawEvent: any) {
  if (!rawEvent || typeof rawEvent !== "object" || typeof rawEvent.id !== "string") {
    return false;
  }

  const pending = corePendingRequests.get(rawEvent.id);
  if (!pending) return false;
  corePendingRequests.delete(rawEvent.id);
  clearTimeout(pending.timeoutId);

  if (typeof rawEvent.type === "string" && rawEvent.type.endsWith(".error")) {
    const error = new Error(rawEvent.error?.message || "Core request failed") as Error & {
      code?: string;
      retryable?: boolean;
      scope?: string;
      holderClientId?: string;
    };
    if (typeof rawEvent.error?.code === "string") {
      error.code = rawEvent.error.code;
    }
    if (typeof rawEvent.error?.retryable === "boolean") {
      error.retryable = rawEvent.error.retryable;
    }
    if (typeof rawEvent.error?.scope === "string") {
      error.scope = rawEvent.error.scope;
    }
    if (typeof rawEvent.error?.holderClientId === "string") {
      error.holderClientId = rawEvent.error.holderClientId;
    }
    if (pending.type === "prompt.send") {
      captureAgentTelemetry("agent_prompt_failed", {
        ...telemetryWorkspaceContext(),
        request_id: rawEvent.id,
        error_code: classifyAgentError(error),
        duration_ms: Date.now() - pending.startedAt,
      });
      captureAgentException(error, {
        ...telemetryWorkspaceContext(),
        request_id: rawEvent.id,
        request_type: pending.type,
        agent_failure_stage: "core_response_error",
        error_code: classifyAgentError(error),
        duration_ms: Date.now() - pending.startedAt,
      });
      settlePromptCompletion(rawEvent.id, error);
      if (activePromptTelemetry?.requestId === rawEvent.id) {
        activePromptTelemetry = null;
      }
    }
    pending.reject(error);
    return true;
  }

  if (rawEvent.type === "session.resume.ok") {
    const payload = rawEvent.payload && typeof rawEvent.payload === "object"
      ? rawEvent.payload as Record<string, unknown>
      : {};
    const replay = Array.isArray(payload.replay) ? payload.replay : [];
    const snapshot = payload.snapshot;
    applyCoreSnapshot(snapshot);
    for (const frame of replay) {
      handleCoreEvent(frame);
    }
  }

  if (pending.type === "prompt.send") {
    // The core response is only an acceptance acknowledgement. The actual
    // completion is recorded when ACP emits agent_message_end above.
    captureAgentTelemetry("agent_prompt_accepted", {
      ...telemetryWorkspaceContext(),
      request_id: rawEvent.id,
      duration_ms: Date.now() - pending.startedAt,
    });
  }

  pending.resolve(rawEvent.payload);
  return true;
}

async function sendCoreRequestOnce(type: string, payload?: unknown, channel: "control" | "terminal" | "system" = "control"): Promise<any> {
  if (!agentWs || agentWs.readyState !== WebSocket.OPEN) {
    return Promise.reject(new Error("Core websocket is not connected"));
  }

  const id = nextCoreRequestId();
  const frame = {
    id,
    type,
    channel,
    ack: latestCoreSeq,
    payload,
  };
  const timeoutMs = getCoreRequestTimeoutMs(type);
  const startedAt = Date.now();

  if (type === "prompt.send") {
    lastPromptRequestId = id;
    activePromptTelemetry = {
      requestId: id,
      startedAt,
      responseStarted: false,
    };
    captureAgentTelemetry("agent_prompt_sent", {
      ...telemetryWorkspaceContext(),
      request_id: id,
      channel,
      ...summarizePromptPayload(payload),
    });
    promptCompletions.set(id, createPromptCompletion());
  }

  const promise = new Promise((resolve, reject) => {
    const timeoutId = timeoutMs == null
      ? null
      : setTimeout(() => {
          corePendingRequests.delete(id);
          if (type === "prompt.send") {
            captureAgentTelemetry("agent_prompt_failed", {
              ...telemetryWorkspaceContext(),
              request_id: id,
              error_code: "timeout",
              duration_ms: Date.now() - startedAt,
            });
            captureAgentException(new Error("Core request timed out: " + type), {
              ...telemetryWorkspaceContext(),
              request_id: id,
              request_type: type,
              agent_failure_stage: "core_request_timeout",
              error_code: "timeout",
              duration_ms: Date.now() - startedAt,
            });
            if (activePromptTelemetry?.requestId === id) {
              activePromptTelemetry = null;
            }
          }
          reject(new Error(`Core request timed out: ${type}`));
        }, timeoutMs);
    corePendingRequests.set(id, { resolve, reject, timeoutId, type, channel, startedAt });
  });

  try {
    agentWs.send(JSON.stringify(frame));
  } catch (error) {
    const pending = corePendingRequests.get(id);
    if (pending) {
      clearTimeout(pending.timeoutId);
      corePendingRequests.delete(id);
    }
    if (type === "prompt.send") {
      captureAgentTelemetry("agent_prompt_failed", {
        ...telemetryWorkspaceContext(),
        request_id: id,
        error_code: classifyAgentError(error),
        duration_ms: Date.now() - startedAt,
      });
      captureAgentException(error, {
        ...telemetryWorkspaceContext(),
        request_id: id,
        request_type: type,
        agent_failure_stage: "core_request_send",
        error_code: classifyAgentError(error),
        duration_ms: Date.now() - startedAt,
      });
      settlePromptCompletion(id, error);
      if (activePromptTelemetry?.requestId === id) {
        activePromptTelemetry = null;
      }
    }
    throw error;
  }
  return promise;
}

const SESSION_WRITE_BUSY_RETRYABLE_TYPES = new Set([
  "session.start",
  "starter.unpack",
  "session.stop",
  "agent.cwd.set",
  "agent.session.new",
  "agent.session.load",
  "agent.session.resume",
  "prompt.cancel",
]);

function isRetryableSessionWriteBusy(
  type: string,
  error: unknown,
): error is Error & { code?: string; retryable?: boolean; scope?: string } {
  if (!SESSION_WRITE_BUSY_RETRYABLE_TYPES.has(type)) return false;
  if (!(error instanceof Error)) return false;
  const typed = error as Error & { code?: string; retryable?: boolean; scope?: string };
  return typed.code === "session_write_busy" || typed.retryable === true || typed.scope === "session";
}

async function delay(ms: number): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

async function sendCoreRequest(type: string, payload?: unknown, channel: "control" | "terminal" | "system" = "control"): Promise<any> {
  if (type !== "hello" && type !== "session.resume" && agentWsOpenPromise) {
    await agentWsOpenPromise;
  }

  const maxAttempts = SESSION_WRITE_BUSY_RETRYABLE_TYPES.has(type) ? 3 : 1;
  let attempt = 0;

  while (true) {
    try {
      return await sendCoreRequestOnce(type, payload, channel);
    } catch (error) {
      if (!isRetryableSessionWriteBusy(type, error) || attempt >= maxAttempts - 1) {
        throw error;
      }

      attempt += 1;
      const backoffMs = 150 * attempt;
      emitAgentDebug(
        "warn",
        `Retrying core request after transient session write conflict: ${type}`,
        {
          attempt,
          backoffMs,
          code: error.code,
          scope: error.scope,
        },
      );
      await delay(backoffMs);
    }
  }
}

async function sendTerminalInputFrame(data: string): Promise<void> {
  if (!data) return;
  await ensureAgentWs();
  await sendCoreRequest("terminal.input", {
    terminalId: latestTerminalId,
    data,
  }, "terminal");
}

/** Connect the agent WebSocket. Called on-demand by selectAgent and page restore.
 *  Unexpected drops auto-reconnect the transport so the app can replay state from core. */
function ensureAgentWs(): Promise<void> {
  if (agentWs && agentWs.readyState === WebSocket.OPEN) return Promise.resolve();
  if (agentWs && agentWs.readyState === WebSocket.CONNECTING) {
    return agentWsOpenPromise ?? Promise.resolve();
  }
  if (agentWsOpenPromise) return agentWsOpenPromise;
  let connectPhase = true;

  agentWsOpenPromise = new Promise<void>((resolve, reject) => {
    resolveAgentWsOpen = resolve;
    rejectAgentWsOpen = reject;
  });
  agentWsGatewayReadyPromise = new Promise<void>((resolve) => {
    resolveAgentWsGatewayReady = resolve;
  });
  agentWs = new WebSocket(getAgentWsUrl());
  const connectTimeoutId = setTimeout(() => {
    if (!agentWsOpenPromise) return;
    const err = new Error("Core websocket connect timed out");
    reportCoreTransportFailure(err.message);
    rejectAgentWsOpen?.(err);
    resetAgentWsHandshakeState();
    agentWs?.close();
    agentWs = null;
  }, CORE_CONNECT_TIMEOUT_MS);

  agentWs.onmessage = (ev) => {
    resolveAgentWsGatewayReady?.();
    resolveAgentWsGatewayReady = null;
    try {
      const event = JSON.parse(ev.data);
      if (handleCoreResponse(event)) {
        return;
      }
      handleCoreEvent(event);
    } catch {
      // Ignore malformed messages
    }
  };

  agentWs.onclose = (ev) => {
    clearTimeout(connectTimeoutId);
    rejectAllCoreRequests(new Error(`Agent websocket closed: ${ev.reason || ev.code || "unknown"}`));
    const statusBeforeClose = latestConnectionStatus;
    const closedDuringConnect = connectPhase;
    if (closedDuringConnect) {
      const message = `Agent websocket closed during connect: ${ev.reason || "unknown"}`;
      if (isBenignCoreConnectCloseReason(ev.reason || undefined)) {
        latestConnectionStatus = "disconnected";
        setPtyConnectionStatus("disconnected");
        emit("connection-status", "disconnected");
      } else {
        reportCoreTransportFailure(message);
      }
      rejectAgentWsOpen?.(new Error(message));
    }
    connectPhase = false;
    resetAgentWsHandshakeState();
    console.log(`[shim] Agent WS closed (code=${ev.code}, reason="${ev.reason || "none"}", clean=${ev.wasClean})`);
    emitAgentDebug("warn", `Agent WebSocket closed (code=${ev.code}, reason="${ev.reason || "none"}")`);
    agentWs = null;
    if (closedDuringConnect) {
      return;
    }
    if (!statusBeforeClose || statusBeforeClose === "disconnected" || statusBeforeClose === "error") {
      return;
    }
    scheduleAgentWsReconnect(`socket closed (${ev.code})`);
  };

  agentWs.onerror = (ev) => {
    clearTimeout(connectTimeoutId);
    const error = new Error("Agent websocket error (" + ((ev as ErrorEvent).type || "unknown") + ")");
    rejectAllCoreRequests(error);
    console.error("[shim] Agent WS error", (ev as ErrorEvent).type);
    captureAgentException(error, {
      ...telemetryWorkspaceContext(),
      agent_failure_stage: "websocket_error",
      error_code: "transport",
    });
    if (!connectPhase && agentWsOpenPromise) {
      reportCoreTransportFailure("Agent websocket error");
      rejectAgentWsOpen?.(new Error(`Agent websocket error`));
      resetAgentWsHandshakeState();
    }
    agentWs?.close();
    agentWs = null;
  };

  if (!agentWs) {
    const err = new Error("Failed to initialize Agent WS");
    rejectAgentWsOpen?.(err);
    agentWsOpenPromise = null;
    return Promise.reject(err);
  }

  agentWs.onopen = () => {
    clearAgentReconnectTimer();
    void (async () => {
      try {
        await agentWsGatewayReadyPromise;
        await sendCoreRequest("hello", {}, "system");
        await sendCoreRequest("session.resume", { lastSeq: latestCoreSeq }, "control");
        clearTimeout(connectTimeoutId);
        connectPhase = false;
        const message = "Agent WS connected";
        resolveAgentWsOpen?.();
        resetAgentWsHandshakeState();
        console.log(`[shim] ${message}`);
      } catch (error) {
        clearTimeout(connectTimeoutId);
        const err = error instanceof Error ? error : new Error(String(error));
        reportCoreTransportFailure(err.message);
        const rejectOpen = rejectAgentWsOpen;
        resetAgentWsHandshakeState();
        agentWs?.close();
        rejectOpen?.(err);
      }
    })();
  };

  return agentWsOpenPromise;
}

// ─── Terminal state over core transport ─────────────────────────────────
let termShouldReconnect = false;
let termCols = 80;
let termRows = 24;
let termReconnectTimer: ReturnType<typeof setTimeout> | null = null;
let termReconnectAttempts = 0;
let termConnectPromise: Promise<void> | null = null;
const termPendingCommands: string[] = [];
let termConnId = 0; // Incremental ID for log correlation
let termLastConnectAt = 0; // Dedup rapid ptySpawn calls (React StrictMode)
// Monotonic generation for the active terminal. A fresh spawn attempt or a
// ptyKill bumps this, which supersedes any in-flight terminal.create adoption:
// under a StrictMode mount → kill → remount race both creates can reach the
// core, so the loser's terminal must be reaped instead of adopted, and its
// later exit/close/output frames must never poison the survivor.
let termAdoptGeneration = 0;

/** True while `terminalId` is the terminal we have adopted for the UI. Frames
 *  for any other id belong to a superseded/killed terminal and are ignored. */
function isActiveTerminalFrame(terminalId: unknown): boolean {
  // Frames without an id predate per-terminal scoping (or come from a core that
  // does not tag them); fall back to accepting them for the adopted terminal.
  if (typeof terminalId !== "string" || terminalId.length === 0) {
    return true;
  }
  return terminalId === latestTerminalId;
}

/** Fire-and-forget close of an orphaned terminal we will not adopt. */
function reapOrphanTerminal(terminalId: string, reason: string) {
  if (!terminalId) return;
  if (!agentWs || agentWs.readyState !== WebSocket.OPEN) return;
  termLog("info", `Reaping superseded terminal ${terminalId} (${reason})`);
  void sendCoreRequest("terminal.close", { terminalId }, "terminal").catch(() => {});
}
let latestPtyConnectionStatus: PtyConnectionStatus = "disconnected";
let latestConnectionStatus = "disconnected";
let latestAuthChallenge: AuthChallenge | null = null;
let termAuthBuffer = "";
let lastAutoCopiedAuthCode: string | null = null;
let lastSelectedAgentPreset: string | null = null;
let copilotPlaintextPromptAnswered = false;
let latestAcpSessionId: string | null = null;
const DEFAULT_AGENT_CAPABILITIES: AgentCapabilities = {
  loadSession: false,
  resume: false,
  listSessions: false,
  cliSessionList: false,
};

function getTermWsUrl(): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}/api/core`;
}

function termLog(level: "info" | "warn" | "error", message: string, data?: unknown) {
  const prefix = `[terminal:${termConnId}]`;
  if (level === "error") {
    console.error(prefix, message, data ?? "");
  } else if (level === "warn") {
    console.warn(prefix, message, data ?? "");
  } else {
    console.log(prefix, message, data ?? "");
  }
  // Also emit to debug panel so user can see terminal lifecycle events
  emit("debug-log", {
    id: crypto.randomUUID(),
    timestamp: Date.now(),
    level,
    source: "terminal",
    message: `${prefix} ${message}`,
    data,
  });
}

function setPtyConnectionStatus(status: PtyConnectionStatus) {
  latestPtyConnectionStatus = status;
  emit("pty-connection-status", status);
}

function stripAnsi(text: string): string {
  return text.replace(/\x1B\[[0-9;?]*[ -/]*[@-~]/g, "");
}

type RuntimeProvider = "sprites" | "gcp_compute" | "e2b" | "daytona" | "lambda_microvms";
type WorkspaceMetadata = {
  provider: RuntimeProvider;
  workspaceId: string | null;
};

const WORKSPACE_PROVIDER_SETTINGS_KEY = "workspaceProvider";
const WORKSPACE_ID_SETTINGS_KEY = "workspaceId";

function normalizeWorkingDirectoryForProvider(provider: RuntimeProvider, value: unknown): string | null {
  if (typeof value !== "string") return null;
  const dir = value.trim();
  if (!dir) return null;

  if (provider === "sprites") {
    if (dir === "/home/user") return "/home/sprite";
    if (dir.startsWith("/home/user/")) {
      return `/home/sprite/${dir.slice("/home/user/".length)}`;
    }
    return dir;
  }

  const home = defaultHomeForProvider(provider);
  if (dir === "/home/user" || dir === "/home/sprite" || dir === "/home/daytona") return home;
  if (dir.startsWith("/home/user/")) {
    return `${home}/${dir.slice("/home/user/".length)}`;
  }
  if (dir.startsWith("/home/sprite/")) {
    return `${home}/${dir.slice("/home/sprite/".length)}`;
  }
  return dir;
}

function normalizeWorkspaceMetadata(value: unknown): WorkspaceMetadata | null | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (value === null) {
    return null;
  }
  if (!value || typeof value !== "object") {
    return undefined;
  }

  const candidate = value as Record<string, unknown>;
  const provider = candidate.provider === "gcp_compute" || candidate.provider === "sprites" || candidate.provider === "e2b" || candidate.provider === "daytona" || candidate.provider === "lambda_microvms"
    ? candidate.provider
    : null;
  if (!provider) {
    return null;
  }

  return {
    provider,
    workspaceId: typeof candidate.workspaceId === "string" && candidate.workspaceId.trim().length > 0
      ? candidate.workspaceId
      : null,
  };
}

function persistWorkspaceMetadata(metadata: WorkspaceMetadata | null) {
  const settings = readSettingsObject();
  settings[WORKSPACE_PROVIDER_SETTINGS_KEY] = metadata?.provider ?? null;
  settings[WORKSPACE_ID_SETTINGS_KEY] = metadata?.workspaceId ?? null;
  writeSettingsObject(settings);
  latestRuntimeState = {
    ...latestRuntimeState,
    workspace: {
      provider: metadata?.provider ?? null,
      workspaceId: metadata?.workspaceId ?? null,
    },
  };
  emit("runtime-state", latestRuntimeState);
}

function readWorkspaceMetadata(): WorkspaceMetadata | null {
  const settings = readSettingsObject();
  return normalizeWorkspaceMetadata({
    provider: settings[WORKSPACE_PROVIDER_SETTINGS_KEY],
    workspaceId: settings[WORKSPACE_ID_SETTINGS_KEY],
  }) ?? null;
}

function getCurrentProvider(): RuntimeProvider {
  return readWorkspaceMetadata()?.provider ?? "gcp_compute";
}

async function copyTextBestEffort(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

function detectTerminalAuthChallenge(chunk: string) {
  if (!chunk) return;
  if (!latestAuthChallenge && !latestConnectionStatus.startsWith("authenticating")) {
    return;
  }

  termAuthBuffer = stripAnsi(`${termAuthBuffer}${chunk}`).slice(-12_000);

  const keychainPrompt = /system\s+keychain\s+unavailable\.\s*store\s+token\s+in\s+plaintext\s+config\s+file\?\s*\(y\/N\)/i.test(termAuthBuffer);
  if (keychainPrompt && !copilotPlaintextPromptAnswered) {
    copilotPlaintextPromptAnswered = true;
    void sendTerminalInputFrame("y\r")
      .then(() => {
        termLog("info", "Detected Copilot keychain prompt; auto-confirmed plaintext token storage");
      })
      .catch(() => {
        termLog("warn", "Detected Copilot keychain prompt but terminal socket is not open for auto-confirm");
      });
  }

  const loginSuccess = /(?:successfully\s+logged\s+in|logged\s+in\s+successfully|login\s+successful|authentication\s+successful|signed\s+in\s+successfully|successfully\s+signed\s+in|API\s+key\s+saved|logged\s+in\s+(?:as|with)\s+\S+|Logging\s+in\s+with\s+Google)/im.test(termAuthBuffer);
  if (loginSuccess) {
    termLog("info", "Detected successful terminal login output");
    latestAuthChallenge = null;
    termAuthBuffer = "";
    lastAutoCopiedAuthCode = null;
    copilotPlaintextPromptAnswered = false;
    emit("auth-challenge", null);
    // The core owns auth completion: it polls the provider's auth status and
    // starts ACP exactly once when the login becomes valid. Starting here as
    // well races that poll and launches a second Codex process.
    return;
  }

  // Device-code / OAuth flows (Copilot, Codex, Claude, Gemini, etc.) surface URL + code in terminal output.
  const urlMatch =
    termAuthBuffer.match(/https?:\/\/auth\.openai\.com[^\s"')\]]*/i) ||
    termAuthBuffer.match(/https?:\/\/chatgpt\.com[^\s"')\]]*/i) ||
    termAuthBuffer.match(/https:\/\/github\.com\/login\/device[^\s"')\]]*/i) ||
    termAuthBuffer.match(/https:\/\/claude\.ai\/oauth[^\s"')\]]*/i) ||
    termAuthBuffer.match(/https:\/\/platform\.claude\.ai[^\s"')\]]*/i) ||
    termAuthBuffer.match(/https:\/\/accounts\.google\.com\/o\/oauth2[^\s"')\]]*/i);
  const codeMatch =
    // Handles lines like:
    // "Enter this one-time code (expires in 15 minutes)"
    // "1ZN9-N3EIA"
    termAuthBuffer.match(
      /(?:enter(?:\s+this)?(?:\s+one-time)?\s+code|one-time\s+code|device\s+code|user[_\s-]?code|verification\s+code|code)\s*(?:\([^)]*\))?\s*[:\-]?\s*([A-Z0-9]{4,}(?:-[A-Z0-9]{3,})+)/i,
    ) ??
    // Standalone device code on its own line
    termAuthBuffer.match(/^\s*([A-Z0-9]{4,}(?:-[A-Z0-9]{3,})+)\s*$/m) ??
    // Legacy Copilot-style fallback
    termAuthBuffer.match(/\b([A-Z0-9]{4}-[A-Z0-9]{4})\b/);

  const detectedUrl = urlMatch?.[0];
  const detectedCode = codeMatch?.[1];
  if (!detectedUrl && !detectedCode) return;

  const base: AuthChallenge = latestAuthChallenge ?? {
    id: crypto.randomUUID(),
    agentName: "AI Agent",
    url: "",
    rawOutput: "",
  };

  const nextChallenge: AuthChallenge = {
    ...base,
    id: base.id || crypto.randomUUID(),
    url: detectedUrl || base.url,
    code: detectedCode || base.code,
    rawOutput: termAuthBuffer,
  };

  const changed =
    !latestAuthChallenge ||
    latestAuthChallenge.url !== nextChallenge.url ||
    latestAuthChallenge.code !== nextChallenge.code;

  if (changed) {
    latestAuthChallenge = nextChallenge;
    emit("auth-challenge", nextChallenge);
  }

  const codeToCopy = nextChallenge.code?.trim();
  if (codeToCopy && codeToCopy !== lastAutoCopiedAuthCode) {
    lastAutoCopiedAuthCode = codeToCopy;
    void copyTextBestEffort(codeToCopy).then((ok) => {
      if (ok) {
        termLog("info", `Detected auth code and copied it to clipboard (${codeToCopy})`);
      } else {
        termLog("warn", `Detected auth code (${codeToCopy}) but browser blocked automatic clipboard copy`);
      }
    });
  }
}

function flushPendingTermCommands() {
  if (!agentWs || agentWs.readyState !== WebSocket.OPEN) return;
  while (termPendingCommands.length > 0) {
    const cmd = termPendingCommands.shift();
    if (!cmd) continue;
    void sendTerminalInputFrame(`${cmd}\r`).catch(() => {
      termPendingCommands.unshift(cmd);
    });
  }
}

async function connectTermWs(): Promise<void> {
  if (latestPtyConnectionStatus === "connected" && latestTerminalId) {
    return;
  }
  if (termConnectPromise) {
    return await termConnectPromise;
  }
  const now = Date.now();
  if (now - termLastConnectAt < 500 && latestPtyConnectionStatus === "connecting") {
    return;
  }
  termLastConnectAt = now;
  if (termReconnectTimer) {
    clearTimeout(termReconnectTimer);
    termReconnectTimer = null;
  }

  termConnId += 1;
  setPtyConnectionStatus("connecting");
  termLog("info", `Connecting to ${getTermWsUrl()} via core transport${termReconnectAttempts > 0 ? ` (reconnect attempt ${termReconnectAttempts})` : ""}`);
  termConnectPromise = (async () => {
    await ensureAgentWs();
    if (latestPtyConnectionStatus === "connected" && latestTerminalId) {
      return;
    }
    // Claim a fresh adoption generation for this create attempt. A concurrent
    // ptyKill (StrictMode cleanup) or a later spawn bumps the generation again,
    // which means the terminal this create returns must be reaped rather than
    // adopted — otherwise the loser of the race poisons the live terminal.
    termAdoptGeneration += 1;
    const attemptGeneration = termAdoptGeneration;
    // The id we were already re-attached to (from a resume snapshot), if any.
    // If the core hands that same id back, we are re-attaching to a terminal it
    // already owns — not creating one, so we must not claim its lifecycle.
    const reattachTerminalId = latestTerminalId;
    const result = await sendCoreRequest("terminal.create", {
      cols: termCols,
      rows: termRows,
      terminalId: latestTerminalId,
    }, "terminal") as { terminalId?: string | null } | undefined;

    const createdId = typeof result?.terminalId === "string" ? result.terminalId : "";

    if (attemptGeneration !== termAdoptGeneration) {
      // Superseded while the create was in flight: adopt nothing, reap the PTY.
      if (createdId && createdId !== latestTerminalId) {
        reapOrphanTerminal(createdId, "terminal.create resolved for a superseded attempt");
      }
      return;
    }

    if (createdId) {
      latestTerminalId = createdId;
      // Only claim ownership when the core minted a genuinely fresh PTY for us.
      // A create that returns the id we passed in is a re-attach to an existing,
      // runtime-owned terminal, which we must leave alive on unmount.
      termCreatedByThisPage = createdId !== reattachTerminalId;
      setPtyConnectionStatus("connected");
      flushPendingTermCommands();
    }
  })().finally(() => {
    termConnectPromise = null;
  });
  await termConnectPromise;
}

function normalizeWorkingDirectory(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const dir = value.trim();
  return dir || null;
}

const SETTINGS_STORAGE_KEY = "freshvibe:settings";
const SESSION_MESSAGES_PREFIX = "freshvibe:session-messages:";
const SESSION_DEBUG_PREFIX = "freshvibe:session-debug:";

function sessionMessagesKey(sessionId: string): string {
  return `${SESSION_MESSAGES_PREFIX}${sessionId}`;
}

function sessionDebugKey(sessionId: string): string {
  return `${SESSION_DEBUG_PREFIX}${sessionId}`;
}

function readSettingsObject(): Record<string, unknown> {
  try {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeSettingsObject(settings: Record<string, unknown>) {
  localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
}

function readSessionPayload(key: string): unknown[] | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return null;
  }
}

function writeSessionPayload(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(Array.isArray(value) ? value : []));
  } catch {
    // localStorage may be unavailable or quota-limited
  }
}

// ─── Guard against concurrent selectAgent calls ────────────────────────
let selectAgentInFlight = false;

// ─── The shim ───────────────────────────────────────────────────────────
const freshvibeShim: FreshVibeAPI = {
  // ── Agent lifecycle ──
  selectAgent: async (preset: string, options?: { resumeSessionId?: string }) => {
    lastSelectedAgentPreset = preset;
    agentShouldReconnect = true;
    clearAgentReconnectTimer();
    // Prevent concurrent selectAgent calls (e.g. from settings restore + user click)
    if (selectAgentInFlight) {
      console.log(`[shim] selectAgent("${preset}") skipped — already in flight`);
      return;
    }
    selectAgentInFlight = true;
    console.log(`[shim] selectAgent("${preset}")${options?.resumeSessionId ? ` resumeSession=${options.resumeSessionId}` : ""}`);
    try {
      await ensureAgentWs();
      emit("connection-status", "connecting");

      // Get the current working directory from settings
      const settings = await freshvibeShim.getSettings();
      const provider = getCurrentProvider();
      const cwd = normalizeWorkingDirectoryForProvider(provider, settings.workingDirectory)
        || defaultHomeForProvider(provider);

      const result = await sendCoreRequest("session.start", {
        presetId: preset,
        cwd,
        ...(options?.resumeSessionId ? { resumeSessionId: options.resumeSessionId } : {}),
      }, "control");
      // Capture the ACP session ID (may be the resumed session or a fresh one)
      if (result && typeof result === "object" && "sessionId" in result && result.sessionId) {
        latestAcpSessionId = result.sessionId;
        emit("acp-session-id", result.sessionId);
      }
      // Status updates will arrive via WebSocket
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to connect agent";
      emit("error", msg);
      emit("connection-status", "error");
      throw err;
    } finally {
      selectAgentInFlight = false;
    }
  },

  installAgent: async (preset: string) => {
    console.log(`[shim] installAgent("${preset}")`);
    emit("connection-status", "installing");
    try {
      await ensureAgentWs();
      emit("debug-log", {
        id: crypto.randomUUID(), timestamp: Date.now(), level: "info",
        source: "system", message: `Installing agent: ${preset}`,
      });
      emit("install-progress", {
        step: "installing",
        message: "Installing in cloud sandbox...",
        progress: 20,
      });

      const data = await sendCoreRequest("agent.install", {
        presetId: preset,
      }, "control");
      emit("debug-log", {
        id: crypto.randomUUID(), timestamp: Date.now(), level: "info",
        source: "system", message: `Install response: ${JSON.stringify(data)}`,
      });

      if (data.success) {
        if (data.output) {
          emit("debug-log", {
            id: crypto.randomUUID(), timestamp: Date.now(), level: "info",
            source: "system", message: `npm output: ${data.output}`,
          });
        }
        emit("install-progress", {
          step: "complete",
          message: `${data.agentName || preset} installed`,
          progress: 100,
        });
        return true;
      } else {
        const errorDetail = [
          data.cmd ? `Command: ${data.cmd}` : null,
          data.exitCode != null ? `Exit code: ${data.exitCode}` : null,
          data.error || "Installation failed",
        ].filter(Boolean).join("\n");
        emit("debug-log", {
          id: crypto.randomUUID(), timestamp: Date.now(), level: "error",
          source: "system", message: `Install failed:\n${errorDetail}`,
        });
        emit("install-progress", { step: "error", message: data.error || "Installation failed", progress: 0 });
        emit("connection-status", "error");
        emit("error", `Install failed: ${data.error || "Unknown error"}`);
        return false;
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Install failed";
      emit("debug-log", {
        id: crypto.randomUUID(), timestamp: Date.now(), level: "error",
        source: "system", message: `Install exception: ${msg}`,
      });
      emit("install-progress", { step: "error", message: msg, progress: 0 });
      emit("connection-status", "error");
      emit("error", msg);
      return false;
    }
  },

  sendPrompt: async (payload) => {
    lastPromptRequestId = null;
    try {
      await ensureAgentWs();
      await sendCoreRequest("prompt.send", payload, "control");
      const requestId = lastPromptRequestId;
      if (requestId) {
        const completion = promptCompletions.get(requestId);
        if (completion) {
          await completion.promise;
        }
      }
    } catch (error) {
      // If no frame was created, the gateway cannot correlate the failure.
      // Emit a separate transport event so a failed connect is still visible.
      if (!lastPromptRequestId) {
        captureAgentTelemetry("agent_prompt_failed", {
          ...telemetryWorkspaceContext(),
          request_id: "not_sent",
          error_code: classifyAgentError(error),
        });
        captureAgentException(error, {
          ...telemetryWorkspaceContext(),
          request_id: "not_sent",
          agent_failure_stage: "prompt_send",
          error_code: classifyAgentError(error),
        });
      }
      throw error;
    }
  },

  cancelPrompt: async () => {
    await ensureAgentWs();
    await sendCoreRequest("prompt.cancel", {}, "control");
    return true;
  },

  respondToPermission: async (id: string, optionId: string | null) => {
    await ensureAgentWs();
    await sendCoreRequest("agent.permission.respond", { id, optionId }, "control");
  },

  respondToAuth: async (_id: string, _result: string) => {
    // Auth is handled server-side via ACP authenticate()
  },

  // ── Model management ──
  getAvailableModels: async () => {
    try {
      await ensureAgentWs();
      return await sendCoreRequest("agent.models.get", {}, "control");
    } catch {
      return null;
    }
  },

  setModel: async (modelId: string) => {
    await ensureAgentWs();
    const result = await sendCoreRequest("agent.models.set", { modelId }, "control");
    return result.success ?? false;
  },

  refreshModels: async () => {
    try {
      await ensureAgentWs();
      return await sendCoreRequest("agent.models.get", {}, "control");
    } catch {
      return null;
    }
  },

  onModelsAvailable: (cb) => on("models-available", cb),

  // ── Harness management ──
  getHarnessStates: async () => {
    await ensureAgentWs();
    return await sendCoreRequest("agent.harness.list", {}, "control");
  },

  checkHarness: async (presetId: string) => {
    await ensureAgentWs();
    return await sendCoreRequest("agent.harness.get", { presetId }, "control");
  },

  stopAgent: async () => {
    disableAgentReconnect();
    latestConnectionStatus = "disconnected";
    if (agentWs?.readyState === WebSocket.OPEN) {
      await sendCoreRequest("session.stop", {}, "control").catch(() => {});
    }
    emit("connection-status", "disconnected");
  },

  logoutAgent: async () => {
    disableAgentReconnect();
    latestConnectionStatus = "disconnected";
    await ensureAgentWs().catch(() => {});
    if (agentWs?.readyState === WebSocket.OPEN) {
      await sendCoreRequest("session.stop", {}, "control").catch(() => {});
    }
    emit("connection-status", "disconnected");
  },
  disconnectWorkspaceRuntime: async () => {
    disableAgentReconnect();
    termShouldReconnect = false;
    termPendingCommands.length = 0;
    if (termReconnectTimer) {
      clearTimeout(termReconnectTimer);
      termReconnectTimer = null;
    }
    clearWorkspaceRuntimeState();
    if (agentWs) {
      const ws = agentWs;
      agentWs = null;
      ws.onmessage = null;
      ws.close(1000, "workspace disconnected");
    }
  },

  // ── Starter kits ──
  getStarterKits: async () => {
    try {
      const resp = await fetch("/api/starter-kits", { credentials: "include" });
      return await resp.json();
    } catch {
      return [];
    }
  },

  unpackStarterKit: async (kitId: string, keepGit?: boolean, targetFolder?: string) => {
    await ensureAgentWs();

    const kitsResp = await fetch("/api/starter-kits", { credentials: "include" });
    const kits = await kitsResp.json().catch(() => []) as Array<{
      id: string;
      cloneUrl: string;
      repoFullName: string;
    }>;
    const kit = kits.find((entry) => entry.id === kitId);
    if (!kit) {
      throw new Error("Starter project not found");
    }

    const data = await sendCoreRequest("starter.unpack", {
      kitId,
      cloneUrl: kit.cloneUrl,
      repoFullName: kit.repoFullName,
      keepGit,
      targetFolder,
    }, "control") as { path: string };

    const previewProjectName = targetFolder
      || (typeof data.path === "string" ? data.path.replace(/\/+$/, "").split("/").pop() || null : null);
    if (previewProjectName) {
      void fetch("/api/preview-slugs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ projectName: previewProjectName }),
      }).catch(() => {});
    }

    return data.path;
  },

  // ── Per-folder agent management ──
  ensureAgentForFolder: async () => {},

  newSession: async () => {
    await ensureAgentWs();
    const result = await sendCoreRequest("agent.session.new", {}, "control");

    if (!result.success) {
      throw new Error(typeof result.error === "string" ? result.error : "Failed to create a new session");
    }

    if (typeof result.sessionId !== "string" || !result.sessionId) {
      throw new Error("Agent returned an invalid session id");
    }

    latestAcpSessionId = result.sessionId;
    emit("acp-session-id", result.sessionId);
    return result.sessionId;
  },

  loadSession: async (acpSessionId: string) => {
    try {
      await ensureAgentWs();
      const result = await sendCoreRequest("agent.session.load", { sessionId: acpSessionId }, "control");
      if (result.success) {
        latestAcpSessionId = acpSessionId;
        emit("acp-session-id", acpSessionId);
      }
      return result;
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : "Failed to load session" };
    }
  },

  resumeSession: async (acpSessionId: string) => {
    try {
      await ensureAgentWs();
      const result = await sendCoreRequest("agent.session.resume", { sessionId: acpSessionId }, "control");
      if (result.success) {
        latestAcpSessionId = acpSessionId;
        emit("acp-session-id", acpSessionId);
      }
      return result;
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : "Failed to resume session" };
    }
  },

  listSessions: async () => {
    try {
      await ensureAgentWs();
      const data = await sendCoreRequest("agent.session.list", {}, "control") as { sessions?: AgentSessionInfo[] };
      return Array.isArray(data.sessions) ? data.sessions : [] as AgentSessionInfo[];
    } catch {
      return [];
    }
  },

  getAgentCapabilities: async () => {
    try {
      await ensureAgentWs();
      return await sendCoreRequest("agent.capabilities.get", {}, "control") as AgentCapabilities;
    } catch {
      return { ...DEFAULT_AGENT_CAPABILITIES };
    }
  },

  // ── Working directory ──
  selectWorkingDirectory: async () => {
    const name = prompt("Enter a folder name for your project:", "my-project");
    if (!name) return null;
    const provider = getCurrentProvider();
    return `${defaultHomeForProvider(provider)}/${name}`;
  },
  setWorkingDirectory: async (dir: string, opts?: { skipTerminalReset?: boolean }) => {
    await ensureAgentWs();
    await sendCoreRequest("agent.cwd.set", { cwd: dir, skipTerminalReset: opts?.skipTerminalReset }, "control");
  },

  // ── Paths ──
  getAppPaths: async () => {
    const provider = getCurrentProvider();
    const home = defaultHomeForProvider(provider);
    return {
      home,
      desktop: home,
      appData: home,
    };
  },

  // ── Interactive PTY shell (WebSocket) ──
  // Auto-reconnects on drop (e.g. Vite proxy restart) so the terminal stays alive.
  ptySpawn: async (_cols?: number, _rows?: number) => {
    // Save dimensions for reconnect
    termCols = _cols || 80;
    termRows = _rows || 24;
    termShouldReconnect = true;
    termReconnectAttempts = 0;
    await connectTermWs();
  },
  ptyRunCommand: async (command: string) => {
    const cmd = command.trim();
    if (!cmd) return;

    if (latestPtyConnectionStatus === "connected" && agentWs?.readyState === WebSocket.OPEN) {
      await sendTerminalInputFrame(`${cmd}\r`);
      return;
    }

    termPendingCommands.push(cmd);
    if (termShouldReconnect) {
      await freshvibeShim.ptySpawn(termCols, termRows).catch(() => {});
    }
  },
  ptyInput: (data: string) => {
    void sendTerminalInputFrame(data).catch(() => {});
  },
  ptyResize: (cols: number, rows: number) => {
    termCols = cols;
    termRows = rows;
    if (agentWs?.readyState === WebSocket.OPEN) {
      void sendCoreRequest("terminal.resize", {
        terminalId: latestTerminalId,
        cols,
        rows,
      }, "terminal").catch(() => {});
    }
  },
  ptyKill: async () => {
    // Reset terminal state synchronously so a subsequent ptySpawn (e.g. a React
    // StrictMode remount) starts from a clean disconnected slate. The
    // terminal.close round-trip is fired-and-forgotten: awaiting it here would
    // let its continuation clobber a freshly spawned terminal's state.
    termShouldReconnect = false;
    termPendingCommands.length = 0;
    if (termReconnectTimer) {
      clearTimeout(termReconnectTimer);
      termReconnectTimer = null;
    }
    const closingTerminalId = latestTerminalId;
    // A terminal we only re-attached to (adopted from a resume snapshot) is a
    // shared, persistent shell the runtime keeps alive across websocket detach.
    // Unmounting this page must detach locally without killing it — otherwise a
    // hard reload while the shell is live, or a StrictMode mount/cleanup cycle,
    // would SIGTERM the live shell (and the 5s SIGKILL fallback surfaces as a
    // spurious "[shell exited]" banner ~5s after the page reattaches).
    const ownsTerminal = termCreatedByThisPage;
    termConnectPromise = null;
    termLastConnectAt = 0;
    latestTerminalId = null;
    termCreatedByThisPage = false;
    // Cancel adoption of any create still in flight for the terminal we are
    // killing: when its response lands it will be reaped, not adopted.
    termAdoptGeneration += 1;
    setPtyConnectionStatus("disconnected");
    if (ownsTerminal && closingTerminalId && agentWs?.readyState === WebSocket.OPEN) {
      void sendCoreRequest("terminal.close", {
        terminalId: closingTerminalId,
      }, "terminal").catch(() => {});
    }
  },
  onPtyOutput: (cb) => on("pty-output", cb),
  onPtyExit: (cb) => on("pty-exit", cb),
  onPtyConnectionStatus: (cb) => {
    const unsub = on("pty-connection-status", cb);
    cb(latestPtyConnectionStatus);
    return unsub;
  },
  onPortsChanged: (cb: (ports: number[]) => void) => {
    const unsub = on("ports-changed", cb);
    cb([...latestPorts]);
    return unsub;
  },
  onPreviewMetadataChanged: (cb) => {
    const unsub = on("preview-metadata-changed", cb);
    cb(latestPreviewMetadata ? { ...latestPreviewMetadata, slugs: [...latestPreviewMetadata.slugs] } : null);
    return unsub;
  },

  // ── Settings persistence (localStorage) ──
  getSettings: async () => {
    try {
      const defaults = {
        selectedAgent: null,
        selectedModel: null,
        workingDirectory: null,
        setupComplete: false,
        permissionPresets: {},
      };
      const settings = readSettingsObject();
      const provider = getCurrentProvider();
      const normalizedWorkingDirectory = normalizeWorkingDirectoryForProvider(
        provider,
        settings.workingDirectory,
      ) ?? normalizeWorkingDirectory(settings.workingDirectory);
      if (settings.workingDirectory !== normalizedWorkingDirectory) {
        settings.workingDirectory = normalizedWorkingDirectory;
        writeSettingsObject(settings);
      }

      return {
        ...defaults,
        ...settings,
        workingDirectory: normalizedWorkingDirectory,
      };
    } catch {
      return {
        selectedAgent: null,
        selectedModel: null,
        workingDirectory: null,
        setupComplete: false,
        permissionPresets: {},
      };
    }
  },
  saveSettings: async (key: string, value: unknown) => {
    try {
      // For workingDirectory, resolve the normalized value FIRST (async),
      // then re-read settings and write — avoids a stale-snapshot race
      // when multiple saveSettings calls run in parallel via Promise.all.
      let resolvedValue = value;
      if (key === "workingDirectory") {
        const provider = getCurrentProvider();
        resolvedValue = normalizeWorkingDirectoryForProvider(provider, value)
          ?? normalizeWorkingDirectory(value);
      }
      // Re-read settings AFTER any async work so we don't clobber concurrent writes
      const settings = readSettingsObject();
      settings[key] = resolvedValue;
      writeSettingsObject(settings);
    } catch {
      // localStorage may not be available
    }
  },
  getSessionMessages: async (sessionId: string) => {
    const current = readSessionPayload(sessionMessagesKey(sessionId));
    if (current) return current;

    const settings = readSettingsObject();
    const legacyKey = `chatMessages:${sessionId}`;
    const legacy = settings[legacyKey];
    if (Array.isArray(legacy)) {
      writeSessionPayload(sessionMessagesKey(sessionId), legacy);
      delete settings[legacyKey];
      writeSettingsObject(settings);
      return legacy;
    }

    return [];
  },
  saveSessionMessages: async (sessionId: string, messages: unknown) => {
    writeSessionPayload(sessionMessagesKey(sessionId), messages);
    const settings = readSettingsObject();
    delete settings[`chatMessages:${sessionId}`];
    writeSettingsObject(settings);
  },
  deleteSessionMessages: async (sessionId: string) => {
    try {
      localStorage.removeItem(sessionMessagesKey(sessionId));
      const settings = readSettingsObject();
      delete settings[`chatMessages:${sessionId}`];
      writeSettingsObject(settings);
    } catch {
      // localStorage may not be available
    }
  },
  getSessionDebugLog: async (sessionId: string) => {
    const current = readSessionPayload(sessionDebugKey(sessionId));
    if (current) return current;

    const settings = readSettingsObject();
    const legacyKeys = [`debugEntries:${sessionId}`, `debugLog:${sessionId}`];
    for (const key of legacyKeys) {
      const legacy = settings[key];
      if (Array.isArray(legacy)) {
        writeSessionPayload(sessionDebugKey(sessionId), legacy);
        delete settings[key];
        writeSettingsObject(settings);
        return legacy;
      }
    }

    return [];
  },
  saveSessionDebugLog: async (sessionId: string, entries: unknown) => {
    writeSessionPayload(sessionDebugKey(sessionId), entries);
    const settings = readSettingsObject();
    delete settings[`debugEntries:${sessionId}`];
    delete settings[`debugLog:${sessionId}`];
    writeSettingsObject(settings);
  },
  deleteSessionDebugLog: async (sessionId: string) => {
    try {
      localStorage.removeItem(sessionDebugKey(sessionId));
      const settings = readSettingsObject();
      delete settings[`debugEntries:${sessionId}`];
      delete settings[`debugLog:${sessionId}`];
      writeSettingsObject(settings);
    } catch {
      // localStorage may not be available
    }
  },
  getClaudeProviderConfig: async () => {
    const settings = await freshvibeShim.getSettings();
    return (settings.claudeProvider as any) ?? {
      provider: "anthropic",
      awsRegion: "",
      smallFastRegion: "",
      authMethod: "aws-profile",
      awsProfile: "",
      awsBedrockApiKey: "",
      awsAccessKeyId: "",
      awsSecretAccessKey: "",
      awsSessionToken: "",
    };
  },
  setClaudeProviderConfig: async (config: unknown) => {
    await freshvibeShim.saveSettings("claudeProvider", config);
  },

  // ── Open in external app (no-ops for web) ──
  openInFinder: async () => {},
  openInEditor: async () => {},

  // ── Agent status check (for reconnect logic) ──
  getAgentStatus: async () => {
    try {
      await ensureAgentWs();
      return await sendCoreRequest("agent.status.get", {}, "control");
    } catch {
      return { status: "disconnected", presetId: null, sessionId: null };
    }
  },
  getAgentState: async () => {
    try {
      await ensureAgentWs();
      return await sendCoreRequest("agent.state.get", {}, "control") as AgentState;
    } catch {
      return {
        connected: false,
        presetId: null,
        cwd: null,
        sessionId: null,
        capabilities: { ...DEFAULT_AGENT_CAPABILITIES },
      };
    }
  },
  bootstrapRuntime: async (request: RuntimeBootstrapRequest) => {
    lastSelectedAgentPreset = request.presetId;
    agentShouldReconnect = true;
    clearAgentReconnectTimer();
    await ensureAgentWs();

    let starterPayload: {
      kitId: string;
      cloneUrl: string;
      repoFullName: string;
      keepGit?: boolean;
      targetFolder?: string;
    } | null = null;

    if (request.starter) {
      const kitsResp = await fetch("/api/starter-kits", { credentials: "include" });
      const kits = await kitsResp.json().catch(() => []) as Array<{
        id: string;
        cloneUrl: string;
        repoFullName: string;
      }>;
      const kit = kits.find((entry) => entry.id === request.starter?.kitId);
      if (!kit) {
        throw new Error("Starter project not found");
      }
      starterPayload = {
        kitId: kit.id,
        cloneUrl: kit.cloneUrl,
        repoFullName: kit.repoFullName,
        keepGit: request.starter.keepGit,
        targetFolder: request.starter.targetFolder,
      };
    }

    const result = await sendCoreRequest("runtime.bootstrap", {
      presetId: request.presetId,
      cwd: request.cwd,
      workspace: request.workspace,
      starter: starterPayload,
      resumeSessionId: request.resumeSessionId,
      reason: request.reason,
    }, "control") as {
      sessionId: string | null;
      cwd: string;
      state: "ready" | "auth_required";
    };

    if (result.sessionId) {
      latestAcpSessionId = result.sessionId;
      emit("acp-session-id", result.sessionId);
    }
    if (request.starter?.targetFolder) {
      void fetch("/api/preview-slugs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ projectName: request.starter.targetFolder }),
      }).catch(() => {});
    }
    return result;
  },
  getRuntimeState: async () => ({ ...latestRuntimeState, workspace: { ...latestRuntimeState.workspace } }),

  /** Connect the agent WebSocket without starting/restarting the agent.
   *  Used on page refresh to pick up an already-running agent's status. */
  connectAgentWs: async () => {
    if (lastSelectedAgentPreset) {
      agentShouldReconnect = true;
    }
    await ensureAgentWs();
  },

  // ── Events ──
  onSessionUpdate: (cb) => on("session-update", cb),
  onPermissionRequest: (cb) => on("permission-request", cb),
  onAuthChallenge: (cb) => on("auth-challenge", cb),
  onConnectionStatus: (cb) => on("connection-status", cb),
  onInstallProgress: (cb) => on("install-progress", cb),
  onError: (cb) => on("error", cb),
  onDebugLog: (cb) => on("debug-log", cb),
  onAcpSessionId: (cb) => on("acp-session-id", cb),
  onRuntimeStateChanged: (cb) => {
    cb({ ...latestRuntimeState, workspace: { ...latestRuntimeState.workspace } });
    return on("runtime-state", cb);
  },

  // ── Utilities ──
  openExternal: async (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  },
  copyToClipboard: async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
  },

  platform: "web",
};

export function installFreshvibeShim() {
  if (typeof window !== "undefined" && !(window as any).freshvibe) {
    (window as any).freshvibe = freshvibeShim;
  }
}

// Auto-install by default for side-effect imports.
installFreshvibeShim();
